import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCircleIcon,
  ShieldCheckIcon,
  BellIcon,
  Cog6ToothIcon,
  PhoneArrowUpRightIcon,
  FingerPrintIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  CalendarDaysIcon, // 👈 This line was missing!
} from '@heroicons/react/24/outline';

// Enhanced settings tabs with vibrant icons and descriptions
const settingsTabs = [
  { name: 'Profile', icon: UserCircleIcon, description: 'Manage your personal details & preferences.' },
  { name: 'Security & Access', icon: ShieldCheckIcon, description: 'Update password, manage mobile access.' },
  { name: 'Notifications', icon: BellIcon, description: 'Configure alerts for various HR events.' },
  { name: 'System Preferences', icon: Cog6ToothIcon, description: 'Adjust application-wide settings.' },
  // Add more tabs based on HRMS features if needed (e.g., 'Permissions & Roles', 'Integrations')
];

const Settings = () => {
  const [activeTab, setActiveTab] = useState('Profile');
  // Profile State
  const [fullName, setFullName] = useState('John Doe');
  const [email, setEmail] = useState('john.doe@example.com');
  const [phone, setPhone] = useState('+1 555-123-4567');

  // Security & Access State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mobileAccessEnabled, setMobileAccessEnabled] = useState(true);
  const [fingerprintLogin, setFingerprintLogin] = useState(false);

  // Notifications State
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [attendanceAlerts, setAttendanceAlerts] = useState(true);
  const [leaveStatusUpdates, setLeaveStatusUpdates] = useState(true);
  const [announcementAlerts, setAnnouncementAlerts] = useState(true);

  // System Preferences State
  const [darkMode, setDarkMode] = useState(false);
  const [language, setLanguage] = useState('English');

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'Profile':
        return (
          <motion.div key="profile" variants={containerVariants} initial="hidden" animate="visible" exit="hidden" className="space-y-8 p-6 bg-white rounded-2xl shadow-xl border border-gray-100">
            <motion.h2 variants={itemVariants} className="text-2xl font-bold text-gray-800 border-b pb-4 mb-4">Personal Information</motion.h2>
            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition duration-150 ease-in-out sm:text-sm p-3"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  id="email"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition duration-150 ease-in-out sm:text-sm p-3"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition duration-150 ease-in-out sm:text-sm p-3"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </motion.div>
            <motion.button
              variants={itemVariants}
              className="mt-8 px-6 py-3 bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold rounded-lg shadow-md hover:from-indigo-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition duration-200 ease-in-out transform hover:scale-105"
            >
              Save Profile Changes
            </motion.button>
          </motion.div>
        );

      case 'Security & Access':
        return (
          <motion.div key="security" variants={containerVariants} initial="hidden" animate="visible" exit="hidden" className="space-y-8 p-6 bg-white rounded-2xl shadow-xl border border-gray-100">
            <motion.h2 variants={itemVariants} className="text-2xl font-bold text-gray-800 border-b pb-4 mb-4">Account Security</motion.h2>

            {/* Password Change */}
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700">Change Password</h3>
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-semibold rounded-lg shadow-md hover:from-purple-600 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-200 ease-in-out transform hover:scale-105">
                Update Password
              </button>
            </motion.div>

            {/* Mobile Access & Biometrics */}
            <motion.div variants={itemVariants} className="space-y-4 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700">Mobile & Biometric Access</h3>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <PhoneArrowUpRightIcon className="h-6 w-6 text-blue-500" />
                  <span className="text-gray-800 font-medium">Secured Mobile Access (Android/iOS)</span>
                </div>
                <button
                  type="button"
                  className={`${
                    mobileAccessEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                  onClick={() => setMobileAccessEnabled(!mobileAccessEnabled)}
                >
                  <span className="sr-only">Enable mobile access</span>
                  <span
                    aria-hidden="true"
                    className={`${
                      mobileAccessEnabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FingerPrintIcon className="h-6 w-6 text-purple-500" />
                  <span className="text-gray-800 font-medium">Fingerprint/Face ID Login</span>
                </div>
                <button
                  type="button"
                  className={`${
                    fingerprintLogin ? 'bg-indigo-600' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                  onClick={() => setFingerprintLogin(!fingerprintLogin)}
                >
                  <span className="sr-only">Enable fingerprint login</span>
                  <span
                    aria-hidden="true"
                    className={`${
                      fingerprintLogin ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </motion.div>
          </motion.div>
        );

      case 'Notifications':
        return (
          <motion.div key="notifications" variants={containerVariants} initial="hidden" animate="visible" exit="hidden" className="space-y-8 p-6 bg-white rounded-2xl shadow-xl border border-gray-100">
            <motion.h2 variants={itemVariants} className="text-2xl font-bold text-gray-800 border-b pb-4 mb-4">Notification Preferences</motion.h2>

            <motion.div variants={itemVariants} className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <BellIcon className="h-6 w-6 text-blue-500" />
                  <span className="text-gray-800 font-medium">Receive all Email Notifications</span>
                </div>
                <button
                  type="button"
                  className={`${
                    emailNotifications ? 'bg-indigo-600' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                  onClick={() => setEmailNotifications(!emailNotifications)}
                >
                  <span className="sr-only">Enable email notifications</span>
                  <span
                    aria-hidden="true"
                    className={`${
                      emailNotifications ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <DocumentTextIcon className="h-6 w-6 text-green-500" />
                  <span className="text-gray-800 font-medium">Attendance & Geofence Alerts</span>
                </div>
                <button
                  type="button"
                  className={`${
                    attendanceAlerts ? 'bg-indigo-600' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                  onClick={() => setAttendanceAlerts(!attendanceAlerts)}
                >
                  <span className="sr-only">Enable attendance alerts</span>
                  <span
                    aria-hidden="true"
                    className={`${
                      attendanceAlerts ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CalendarDaysIcon className="h-6 w-6 text-yellow-500" /> {/* Assuming CalendarDaysIcon for leaves */}
                  <span className="text-gray-800 font-medium">Leave Status Updates</span>
                </div>
                <button
                  type="button"
                  className={`${
                    leaveStatusUpdates ? 'bg-indigo-600' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                  onClick={() => setLeaveStatusUpdates(!leaveStatusUpdates)}
                >
                  <span className="sr-only">Enable leave status updates</span>
                  <span
                    aria-hidden="true"
                    className={`${
                      leaveStatusUpdates ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <BellIcon className="h-6 w-6 text-red-500" />
                  <span className="text-gray-800 font-medium">Announcement Alerts</span>
                </div>
                <button
                  type="button"
                  className={`${
                    announcementAlerts ? 'bg-indigo-600' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                  onClick={() => setAnnouncementAlerts(!announcementAlerts)}
                >
                  <span className="sr-only">Enable announcement alerts</span>
                  <span
                    aria-hidden="true"
                    className={`${
                      announcementAlerts ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </motion.div>
          </motion.div>
        );

      case 'System Preferences':
        return (
          <motion.div key="system" variants={containerVariants} initial="hidden" animate="visible" exit="hidden" className="space-y-8 p-6 bg-white rounded-2xl shadow-xl border border-gray-100">
            <motion.h2 variants={itemVariants} className="text-2xl font-bold text-gray-800 border-b pb-4 mb-4">Application Preferences</motion.h2>

            <motion.div variants={itemVariants} className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <GlobeAltIcon className="h-6 w-6 text-purple-500" />
                  <span className="text-gray-800 font-medium">Language</span>
                </div>
                <select
                  className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  <option>English</option>
                  <option>Hindi</option>
                  <option>Bengali</option>
                </select>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Cog6ToothIcon className="h-6 w-6 text-gray-500" />
                  <span className="text-gray-800 font-medium">Dark Mode</span>
                </div>
                <button
                  type="button"
                  className={`${
                    darkMode ? 'bg-indigo-600' : 'bg-gray-300'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                  onClick={() => setDarkMode(!darkMode)}
                >
                  <span className="sr-only">Enable dark mode</span>
                  <span
                    aria-hidden="true"
                    className={`${
                      darkMode ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </motion.div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="p-6 md:p-10 lg:p-12 min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100" // Vibrant background
    >
      <motion.h1
        variants={itemVariants}
        className="text-4xl font-extrabold text-gray-900 mb-10 pb-4 border-b-2 border-indigo-200"
      >
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-700">
          Application Settings
        </span>
      </motion.h1>

      <div className="lg:flex lg:space-x-10">
        {/* 🔹 Tab Navigation (Stylized) */}
        <motion.div
          variants={itemVariants}
          className="flex-shrink-0 lg:w-72 bg-white rounded-2xl shadow-xl p-6 mb-8 lg:mb-0 border border-gray-100"
        >
          <p className="text-gray-500 text-sm mb-6 uppercase font-bold tracking-wider">Categories</p>
          <div className="space-y-3">
            {settingsTabs.map((tab) => (
              <a
                key={tab.name}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab(tab.name);
                }}
                className={`flex items-center space-x-4 p-4 rounded-xl transition-all duration-300 ease-in-out
                  ${
                    activeTab === tab.name
                      ? 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600'
                  }`}
              >
                <tab.icon
                  className={`h-7 w-7 ${activeTab === tab.name ? 'text-white' : 'text-indigo-500'}`}
                />
                <div>
                  <span className="text-lg font-semibold block">{tab.name}</span>
                  <span className={`text-sm ${activeTab === tab.name ? 'text-indigo-100' : 'text-gray-500'}`}>
                    {tab.description}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </motion.div>

        {/* 🔹 Tab Content */}
        <div className="flex-1">
          <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default Settings;